import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useQuery } from 'react-query';
import { searchAPI } from '../../services/api';

const SearchBar = ({ onSearch, onClose, autoFocus = false, placeholder = "Search products, brands, categories..." }) => {
  const [query, setQuery] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);

  // Fetch search suggestions with better error handling
  const { data: suggestions = [], isLoading: isLoadingSuggestions } = useQuery(
    ['searchSuggestions', query],
    () => searchAPI.getSuggestions(query),
    {
      enabled: query.length >= 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: false, // Disable retries to avoid CORB issues
      onError: (error) => {
        console.warn('Search suggestions error:', error);
      }
    }
  );

  // Disabled popular search terms to avoid CORB issues
  const popularTerms = [];

  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus]);

  useEffect(() => {
    setShowSuggestions(query.length >= 1 && suggestions.length > 0);
    setSelectedIndex(-1);
  }, [query, suggestions]);

  const handleInputChange = (e) => {
    setQuery(e.target.value);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => 
        prev < suggestions.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (selectedIndex >= 0 && suggestions[selectedIndex]) {
        handleSuggestionClick(suggestions[selectedIndex]);
      } else {
        handleSearch();
      }
    } else if (e.key === 'Escape') {
      onClose?.();
    }
  };

  const handleSearch = () => {
    if (query.trim()) {
      onSearch(query.trim());
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setQuery(suggestion);
    onSearch(suggestion);
  };

  const handlePopularTermClick = (term) => {
    setQuery(term);
    onSearch(term);
  };

  const clearQuery = () => {
    setQuery('');
    setSelectedIndex(-1);
    inputRef.current?.focus();
  };

  return (
    <div className="relative w-full">
      {/* Search Input */}
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
          <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
        </div>
        
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => setShowSuggestions(true)}
          placeholder={placeholder}
          className="w-full pl-10 sm:pl-12 pr-10 sm:pr-12 py-2.5 sm:py-4 text-sm sm:text-lg bg-white rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent shadow-medium"
        />
        
        {query && (
          <button
            onClick={clearQuery}
            className="absolute inset-y-0 right-0 pr-4 flex items-center"
          >
            <XMarkIcon className="h-5 w-5 text-gray-400 hover:text-gray-600 transition-colors duration-200" />
          </button>
        )}
      </div>

      {/* Search Suggestions */}
      <AnimatePresence>
        {showSuggestions && (
          <motion.div
            ref={suggestionsRef}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="absolute top-full left-0 right-0 mt-2 bg-white rounded-xl shadow-large border border-gray-100 overflow-hidden z-50"
          >
            {/* Loading State */}
            {isLoadingSuggestions && (
              <div className="p-4 text-center text-gray-500">
                <div className="spinner mx-auto mb-2"></div>
                <p className="text-sm">Searching...</p>
              </div>
            )}

            {/* Suggestions */}
            {!isLoadingSuggestions && suggestions.length > 0 && (
              <div className="py-2">
                <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  Suggestions
                </div>
                {suggestions.map((suggestion, index) => (
                  <button
                    key={suggestion}
                    onClick={() => handleSuggestionClick(suggestion)}
                    className={`w-full px-4 py-3 text-left hover:bg-gray-50 transition-colors duration-200 flex items-center space-x-3 ${
                      index === selectedIndex ? 'bg-primary-50 text-primary-700' : 'text-gray-700'
                    }`}
                  >
                    <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
                    <span className="text-sm">{suggestion}</span>
                  </button>
                ))}
              </div>
            )}

            {/* Popular Search Terms */}
            {!isLoadingSuggestions && suggestions.length === 0 && popularTerms.length > 0 && (
              <div className="py-2">
                <div className="px-4 py-2 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  Popular Searches
                </div>
                <div className="flex flex-wrap gap-2 p-4">
                  {popularTerms.slice(0, 8).map((term) => (
                    <button
                      key={term}
                      onClick={() => handlePopularTermClick(term)}
                      className="px-3 py-1 text-sm bg-gray-100 hover:bg-primary-100 text-gray-700 hover:text-primary-700 rounded-full transition-colors duration-200"
                    >
                      {term}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* No Results */}
            {!isLoadingSuggestions && suggestions.length === 0 && popularTerms.length === 0 && (
              <div className="p-4 text-center text-gray-500">
                <p className="text-sm">No suggestions found</p>
              </div>
            )}

            {/* Search Button */}
            {query.trim() && (
              <div className="border-t border-gray-100 p-2">
                <button
                  onClick={handleSearch}
                  className="w-full px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2"
                >
                  <MagnifyingGlassIcon className="h-4 w-4" />
                  <span>Search "{query}"</span>
                </button>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default SearchBar;
